import { create } from "zustand"
import { mutative } from "zustand-mutative"

type Id = string | number
type SubscriptionStatus = {
  // Whether the subscription is paused
  paused: boolean

  // Whether the the last result on the subscription was an error
  hasError: boolean
}

type SubscriptionState = {
  subscriptions: Map<Id, SubscriptionStatus>
  upsert: (id: Id, status?: Partial<SubscriptionStatus>) => void
  remove: (id: Id) => void
}

const DEFAULT_SUBSCRIPTION_STATUS: SubscriptionStatus = {
  paused: false,
  hasError: false,
}

export const useSubscriptionStore = create<SubscriptionState>()(
  mutative((set, get) => ({
    subscriptions: new Map(),
    upsert: (id, status) => {
      const existingStatus = get().subscriptions.get(id)

      // Don't set the status if it hasn't changed otherwise it will re-render needlessly
      if (
        existingStatus &&
        status?.paused === existingStatus.paused &&
        status.hasError === existingStatus.hasError
      ) {
        return
      }

      set(state => {
        state.subscriptions.set(id, {
          ...(existingStatus ?? DEFAULT_SUBSCRIPTION_STATUS),
          ...status,
        })
      })
    },
    remove: id => {
      set(state => {
        state.subscriptions.delete(id)
      })
    },
  })),
)
